import { Link, useParams } from 'react-router';
import { formatDate, formatInr } from '@mys/contracts/format';
import { ApiError } from '../lib/api.js';
import { INVOICE_STATUS_LABELS, INVOICE_TYPE_LABELS, useInvoice } from '../lib/portal.js';

/* ============================================================================
   /invoices/:id — one bill, in full.
   ----------------------------------------------------------------------------
   **There is no "pay now" button here, and that is deliberate.** Razorpay KYC
   is not live, so `POST /api/payments/order` runs its no-op: it would hand
   back a real-looking order id for a checkout that cannot open and a payment
   nothing can confirm. A button that looks like it takes money and does not is
   worse for a shop owner than no button — they would believe they had paid.

   When KYC clears, the Razorpay checkout goes in right here, and the
   `/payment/success` and `/payment/failed` routes are already waiting as its
   redirect targets. Until then this screen tells the truth: how to pay us, in
   the ways this business actually gets paid.

   See CONTEXT.md §14 questions 4 and 6.
   ========================================================================== */

export function InvoiceDetailPage() {
  const { id } = useParams();
  const invoice = useInvoice(id ?? '');

  if (invoice.isPending) return <p className="muted">Load ho raha hai…</p>;

  if (invoice.error) {
    return (
      <>
        <p className="alert" role="alert">
          {invoice.error instanceof ApiError ? invoice.error.message : 'Yeh bill nahi mila.'}
        </p>
        <Link to="/invoices" className="btn btn--ghost">
          Bills par wapas
        </Link>
      </>
    );
  }

  const item = invoice.data?.invoice;
  if (!item) return null;

  const tax = item.grossInr - item.amountInr;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="kicker">{INVOICE_TYPE_LABELS[item.type] ?? item.type}</p>
          <h1 className="h1">{item.number}</h1>
        </div>
        <span className={item.status === 'due' ? 'pill pill--due' : 'pill'}>
          {INVOICE_STATUS_LABELS[item.status] ?? item.status}
        </span>
      </div>

      <section className="card">
        <dl className="rows">
          <div>
            <dt>Amount</dt>
            <dd>{formatInr(item.amountInr)}</dd>
          </div>
          <div>
            <dt>GST ({item.gstPercent}%)</dt>
            <dd>{formatInr(tax)}</dd>
          </div>
          <div className="rows__total">
            <dt>Total</dt>
            <dd>{formatInr(item.grossInr)}</dd>
          </div>
          <div>
            <dt>Bill ki date</dt>
            <dd>{formatDate(item.createdAt)}</dd>
          </div>
          {item.paidAt ? (
            <div>
              <dt>Mila</dt>
              <dd>{formatDate(item.paidAt)}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      {item.status === 'due' ? (
        <section className="card stack">
          <h2 className="h2">Payment kaise kijiye</h2>
          <p className="muted">
            Abhi online payment chalu nahi hua hai. UPI, bank transfer ya cash — jo aasaan ho, kar
            dijiye aur humein WhatsApp par bata dijiye. Receipt hum bhej denge.
          </p>
          <a
            className="btn"
            href={`https://wa.me/918630415226?text=${encodeURIComponent(
              `Namaste, ${item.number} ka payment karna hai (${formatInr(item.grossInr)}).`,
            )}`}
          >
            WhatsApp par baat kijiye
          </a>
        </section>
      ) : null}

      <Link to="/invoices" className="btn btn--ghost">
        Bills par wapas
      </Link>
    </>
  );
}
